import { localizeReportValue } from "./reportValueCatalog";
import { localizeReportNarrative } from "./reportNarrativeCatalog";
import {
  localizeWealthRecommendedMode,
  resolveWealthCapabilityStrength,
  resolveWealthCapabilityAction,
} from "./reportWealthBlueprintCatalog";
import {
  localizeNumberRelationClass,
} from "./reportNumberAdviceCatalog";

/**
 * I18N-C3-D2 — Sheet25 simple-composite presentation layer.
 *
 * Rules:
 * - Presentation only.
 * - Never imports or executes Sheet25 engine.
 * - Never parses/splits/normalizes/replaces canonical strings.
 * - Rebuilds canonical strings from separately supplied authoritative
 *   components and requires byte-exact equality.
 * - Unknown/missing/mismatched components return canonical unchanged.
 * - Does not cover D3 fields:
 *   riskToleranceDetail, day30Plan, day60Plan, day90Plan, numberCulture.
 */

function finiteNumber(value) {
  return typeof value === "number" && Number.isFinite(value);
}

function translatedValue(domain, value) {
  if (typeof value !== "string") return undefined;

  const localized =
    localizeReportValue("en", domain, value);

  return localized === value ? undefined : localized;
}

function translatedNarrative(domain, value) {
  if (typeof value !== "string") return undefined;

  const localized =
    localizeReportNarrative("en", domain, value);

  return localized === value ? undefined : localized;
}

export function presentSheet25WealthPersonality(
  locale,
  canonical,
  {
    lifeNumber,
    corePosition,
    positionSummary,
  } = {},
) {
  if (typeof canonical !== "string") return canonical;
  if (locale !== "en") return canonical;

  if (
    !finiteNumber(lifeNumber) ||
    typeof corePosition !== "string" ||
    typeof positionSummary !== "string"
  ) {
    return canonical;
  }

  const expectedCanonical =
    `生命数字 ${lifeNumber}｜${corePosition}\n` +
    `策略说明：${positionSummary}`;

  if (canonical !== expectedCanonical) return canonical;

  const position =
    translatedNarrative("sheet13", corePosition);

  const summary =
    translatedNarrative("sheet13", positionSummary);

  if (
    position === undefined ||
    summary === undefined
  ) {
    return canonical;
  }

  return (
    `Life Number ${lifeNumber} | ${position}\n` +
    `Strategy note: ${summary}`
  );
}

export function presentSheet25IncomeTendency(
  locale,
  canonical,
  {
    stableIncomeScore,
    opportunityIncomeScore,
    incomeStable,
  } = {},
) {
  if (typeof canonical !== "string") return canonical;
  if (locale !== "en") return canonical;

  if (
    !finiteNumber(stableIncomeScore) ||
    !finiteNumber(opportunityIncomeScore) ||
    typeof incomeStable !== "boolean"
  ) {
    return canonical;
  }

  const canonicalTail = incomeStable
    ? "当前更适合先稳定主业与现金流，再小步验证项目、合作或副业机会。"
    : "当前具备较强机会拓展倾向，但仍需设置预算、退出条件与现金流底线。";

  const expectedCanonical =
    `稳定收入倾向 ${stableIncomeScore}分｜` +
    `机会型收入倾向 ${opportunityIncomeScore}分\n` +
    canonicalTail;

  if (canonical !== expectedCanonical) return canonical;

  const englishTail = incomeStable
    ? "The current priority is to stabilize primary income and cash flow first, then validate projects, collaborations, or side-income opportunities in small steps."
    : "There is currently a stronger tendency to pursue opportunities, while still requiring clear budget limits, exit conditions, and a cash-flow floor.";

  return (
    `Stable-income tendency: ${stableIncomeScore} | ` +
    `Opportunity-income tendency: ${opportunityIncomeScore}\n` +
    englishTail
  );
}

export function presentSheet25IncomeStructureAdvice(
  locale,
  canonical,
  {
    currentGoal,
    topIndustry,
    topRole,
    incomeStable,
  } = {},
) {
  if (typeof canonical !== "string") return canonical;
  if (locale !== "en") return canonical;

  if (
    typeof currentGoal !== "string" ||
    typeof topIndustry !== "string" ||
    typeof topRole !== "string" ||
    typeof incomeStable !== "boolean"
  ) {
    return canonical;
  }

  const primary = incomeStable
    ? "稳定收入／专业服务"
    : "项目机会／资源整合";

  const secondary = incomeStable
    ? "小额项目验证"
    : "稳定现金流";

  const expectedCanonical =
    `当前主要目标：${currentGoal}｜` +
    `首选行业：${topIndustry}｜` +
    `首选岗位：${topRole}\n` +
    `建议收入结构：以${primary}为主轴，` +
    `以${secondary}作为第二来源。` +
    `所有选择仍需结合技能、市场、资金与家庭责任验证。`;

  if (canonical !== expectedCanonical) return canonical;

  const goal =
    translatedValue("currentGoal", currentGoal);

  const industry =
    translatedValue("industry", topIndustry);

  const role =
    translatedValue("role", topRole);

  if (
    goal === undefined ||
    industry === undefined ||
    role === undefined
  ) {
    return canonical;
  }

  const primaryEn = incomeStable
    ? "stable income / professional services"
    : "project opportunities / resource integration";

  const secondaryEn = incomeStable
    ? "small-scale project validation"
    : "stable cash flow";

  return (
    `Current primary goal: ${goal} | ` +
    `Preferred industry: ${industry} | ` +
    `Preferred role: ${role}\n` +
    `Recommended income structure: use ${primaryEn} as the primary axis, ` +
    `with ${secondaryEn} as the secondary source. ` +
    `All choices should still be validated against skills, market conditions, capital, and family responsibilities.`
  );
}

export function presentSheet25StrongestSummary(
  locale,
  canonical,
  {
    maxLabel,
    maxScore,
  } = {},
) {
  if (typeof canonical !== "string") return canonical;
  if (locale !== "en") return canonical;

  if (
    typeof maxLabel !== "string" ||
    !finiteNumber(maxScore)
  ) {
    return canonical;
  }

  const strength =
    resolveWealthCapabilityStrength(maxLabel);

  if (!strength) return canonical;

  const expectedCanonical =
    `当前最强项：${maxLabel}（${maxScore}分）\n` +
    strength.canonical;

  if (canonical !== expectedCanonical) return canonical;

  const label =
    translatedValue("wealthCapability", maxLabel);

  if (label === undefined) return canonical;

  return (
    `Current strongest capability: ${label} (${maxScore})\n` +
    strength.english
  );
}

export function presentSheet25PriorityReinforcement(
  locale,
  canonical,
  {
    minLabel,
    minScore,
  } = {},
) {
  if (typeof canonical !== "string") return canonical;
  if (locale !== "en") return canonical;

  if (
    typeof minLabel !== "string" ||
    !finiteNumber(minScore)
  ) {
    return canonical;
  }

  const action =
    resolveWealthCapabilityAction(minLabel);

  if (!action) return canonical;

  const expectedCanonical =
    `优先补强：${minLabel}（${minScore}分）\n` +
    `${action.canonical}\n` +
    `提醒：低分代表当前需要更多制度、资源或验证，不代表必然损失。`;

  if (canonical !== expectedCanonical) return canonical;

  const label =
    translatedValue("wealthCapability", minLabel);

  if (label === undefined) return canonical;

  return (
    `Priority development: ${label} (${minScore})\n` +
    `${action.english}\n` +
    `Note: a lower score indicates a current need for stronger systems, resources, or validation; it does not imply an inevitable loss.`
  );
}

export function presentSheet25StageConclusion(
  locale,
  canonical,
  {
    average,
    minLabel,
    maxLabel,
  } = {},
) {
  if (typeof canonical !== "string") return canonical;
  if (locale !== "en") return canonical;

  if (
    !finiteNumber(average) ||
    typeof minLabel !== "string" ||
    typeof maxLabel !== "string"
  ) {
    return canonical;
  }

  const expectedCanonical =
    `五项能力平均 ${average}分。` +
    `当前策略应先补强「${minLabel}」，再放大「${maxLabel}」。\n` +
    `推荐原则：先建立现金流与风险边界，再扩大收入实验；` +
    `只追踪可验证的客户、收入、成本、储备与执行数据。`;

  if (canonical !== expectedCanonical) return canonical;

  const min =
    translatedValue("wealthCapability", minLabel);

  const max =
    translatedValue("wealthCapability", maxLabel);

  if (
    min === undefined ||
    max === undefined
  ) {
    return canonical;
  }

  return (
    `Average across the five capabilities: ${average}. ` +
    `The current strategy should strengthen “${min}” first, then amplify “${max}”.\n` +
    `Recommended principle: establish cash-flow and risk boundaries before expanding income experiments; ` +
    `track only verifiable customer, income, cost, reserve, and execution data.`
  );
}

export function presentSheet25RecommendedModeDetail(
  locale,
  canonical,
  {
    recommendedMode,
    topIndustry,
    topRole,
  } = {},
) {
  if (typeof canonical !== "string") return canonical;
  if (locale !== "en") return canonical;

  if (
    typeof recommendedMode !== "string" ||
    typeof topIndustry !== "string" ||
    typeof topRole !== "string"
  ) {
    return canonical;
  }

  const expectedCanonical =
    `推荐主模式：${recommendedMode}\n` +
    `现实落点：优先结合首选行业「${topIndustry}」` +
    `与岗位「${topRole}」，` +
    `从已有经验最接近、投入可控、能够快速验证的产品或服务开始。`;

  if (canonical !== expectedCanonical) return canonical;

  const mode =
    localizeWealthRecommendedMode(
      "en",
      recommendedMode,
    );

  const industry =
    translatedValue("industry", topIndustry);

  const role =
    translatedValue("role", topRole);

  if (
    mode === recommendedMode ||
    industry === undefined ||
    role === undefined
  ) {
    return canonical;
  }

  return (
    `Recommended primary mode: ${mode}\n` +
    `Practical starting point: combine the preferred industry “${industry}” ` +
    `with the role “${role}”, and begin with a product or service closest to existing experience, ` +
    `with controlled commitment and rapid validation.`
  );
}

export function presentSheet25AnnualRhythmDetail(
  locale,
  canonical,
  {
    personalYear,
    annualTitle,
    annualWealthRhythm,
  } = {},
) {
  if (typeof canonical !== "string") return canonical;
  if (locale !== "en") return canonical;

  if (
    !finiteNumber(personalYear) ||
    typeof annualTitle !== "string" ||
    typeof annualWealthRhythm !== "string"
  ) {
    return canonical;
  }

  const expectedCanonical =
    `个人流年 ${personalYear}｜${annualTitle}\n` +
    `财富节奏：${annualWealthRhythm}\n` +
    `年度建议用于安排学习、验证与复盘节奏，不预测收益事件。`;

  if (canonical !== expectedCanonical) return canonical;

  const title =
    translatedNarrative("sheet12", annualTitle);

  const rhythm =
    translatedNarrative(
      "sheet12",
      annualWealthRhythm,
    );

  if (
    title === undefined ||
    rhythm === undefined
  ) {
    return canonical;
  }

  return (
    `Personal Year ${personalYear} | ${title}\n` +
    `Wealth rhythm: ${rhythm}\n` +
    `Annual guidance is used to structure learning, validation, and review cadence; it does not predict financial-return events.`
  );
}

export function presentSheet25GuiRenResource(
  locale,
  canonical,
  {
    guiRenScore,
    annualFocus,
  } = {},
) {
  if (typeof canonical !== "string") return canonical;
  if (locale !== "en") return canonical;

  if (
    !finiteNumber(guiRenScore) ||
    typeof annualFocus !== "string"
  ) {
    return canonical;
  }

  const expectedCanonical =
    `贵人合作指数 ${guiRenScore}分｜${annualFocus}\n` +
    `资源优先级：客户反馈、专业导师、可信合作方与既有行业网络；` +
    `合作前明确角色、预算、交付标准与退出条件。`;

  if (canonical !== expectedCanonical) return canonical;

  const focus =
    translatedNarrative("sheet13", annualFocus);

  if (focus === undefined) return canonical;

  return (
    `Mentor & collaboration index: ${guiRenScore} | ${focus}\n` +
    `Resource priorities: customer feedback, professional mentors, trusted partners, and existing industry networks; ` +
    `define roles, budget, delivery standards, and exit conditions before collaborating.`
  );
}

export function presentSheet25IntegratedPriority(
  locale,
  canonical,
  {
    minLabel,
    maxLabel,
  } = {},
) {
  if (typeof canonical !== "string") return canonical;
  if (locale !== "en") return canonical;

  if (
    typeof minLabel !== "string" ||
    typeof maxLabel !== "string"
  ) {
    return canonical;
  }

  const minAction =
    resolveWealthCapabilityAction(minLabel);

  const maxStrength =
    resolveWealthCapabilityStrength(maxLabel);

  if (
    !minAction ||
    !maxStrength
  ) {
    return canonical;
  }

  const expectedCanonical =
    `① 先补强：${minAction.canonical}　` +
    `② 再放大：${maxStrength.canonical}　` +
    `③ 仅追踪1–3项可量化指标，并在第30／60／90天复盘。`;

  if (canonical !== expectedCanonical) return canonical;

  return (
    `① Strengthen first: ${minAction.english}　` +
    `② Then amplify: ${maxStrength.english}　` +
    `③ Track only 1–3 measurable indicators and review them on days 30／60／90.`
  );
}

/**
 * I18N-C3-D3 — Sheet25 cross-sheet composite presenters.
 *
 * Presentation only.
 *
 * Rules:
 * - Never executes Sheet14 / Sheet24 / Sheet25 engines.
 * - Never parses, splits, normalizes, trims or replaces canonical strings.
 * - Rebuilds canonical strings from separately supplied authoritative
 *   components and requires byte-exact equality before English rendering.
 * - Sheet24 number-group strings remain opaque strings.
 * - Unknown/missing/mismatched components return canonical unchanged.
 */

function validSheet25Score(value) {
  return (
    typeof value === "number" &&
    Number.isInteger(value) &&
    Number.isFinite(value) &&
    value >= 0 &&
    value <= 100
  );
}

function validLifeNumber(value) {
  return (
    typeof value === "number" &&
    Number.isInteger(value) &&
    Number.isFinite(value) &&
    value >= 1 &&
    value <= 9
  );
}

export function presentSheet25RiskToleranceDetail(
  locale,
  canonical,
  {
    riskControlScore,
    stabilityScore,
    dayMasterStatusText,
    annualRiskNote,
  } = {},
) {
  if (typeof canonical !== "string") return canonical;
  if (locale !== "en") return canonical;

  if (
    !validSheet25Score(riskControlScore) ||
    !validSheet25Score(stabilityScore) ||
    typeof dayMasterStatusText !== "string" ||
    typeof annualRiskNote !== "string"
  ) {
    return canonical;
  }

  const expectedCanonical =
    `风险控制 ${riskControlScore}分｜稳定性 ${stabilityScore}分\n` +
    `日主状态提醒：${dayMasterStatusText}\n` +
    `年度风险：${annualRiskNote}\n` +
    `重大投资、借贷、创业或转职应另行进行现金流测算与专业核查。`;

  if (canonical !== expectedCanonical) return canonical;

  const statusText =
    translatedNarrative(
      "sheet14",
      dayMasterStatusText,
    );

  const riskNote =
    translatedNarrative(
      "sheet14",
      annualRiskNote,
    );

  if (
    statusText === undefined ||
    riskNote === undefined
  ) {
    return canonical;
  }

  return (
    `Risk Control ${riskControlScore} | Stability ${stabilityScore}\n` +
    `Day Master status note: ${statusText}\n` +
    `Annual risk note: ${riskNote}\n` +
    `Major investment, borrowing, entrepreneurship, or career-change decisions should be supported by separate cash-flow analysis and appropriate professional review.`
  );
}

export function presentSheet25Day30Plan(
  locale,
  canonical,
  {
    minLabel,
    day30GoalLink,
    day30Metric,
  } = {},
) {
  if (typeof canonical !== "string") return canonical;
  if (locale !== "en") return canonical;

  if (
    typeof minLabel !== "string" ||
    typeof day30GoalLink !== "string" ||
    typeof day30Metric !== "string"
  ) {
    return canonical;
  }

  const minAction =
    resolveWealthCapabilityAction(minLabel);

  if (!minAction) return canonical;

  const expectedCanonical =
    `盘点近3个月收入、固定支出、债务与现金缓冲，建立财富基线。\n` +
    `优先补强：${minAction.canonical}\n` +
    `当前目标联动：${day30GoalLink}\n` +
    `检查指标：${day30Metric}`;

  if (canonical !== expectedCanonical) return canonical;

  const goal =
    day30GoalLink === "—"
      ? "—"
      : translatedNarrative(
          "sheet14",
          day30GoalLink,
        );

  const metric =
    day30Metric === "—"
      ? "—"
      : translatedNarrative(
          "sheet14",
          day30Metric,
        );

  if (
    goal === undefined ||
    metric === undefined
  ) {
    return canonical;
  }

  return (
    `Review income, fixed expenses, debt, and cash reserves from the past three months to establish a wealth baseline.\n` +
    `Priority development: ${minAction.english}\n` +
    `Current-goal linkage: ${goal}\n` +
    `Check metric: ${metric}`
  );
}

export function presentSheet25Day60Plan(
  locale,
  canonical,
  {
    recommendedMode,
    day60GoalLink,
    day60Metric,
  } = {},
) {
  if (typeof canonical !== "string") return canonical;
  if (locale !== "en") return canonical;

  if (
    typeof recommendedMode !== "string" ||
    typeof day60GoalLink !== "string" ||
    typeof day60Metric !== "string"
  ) {
    return canonical;
  }

  const expectedCanonical =
    `围绕「${recommendedMode}」完成一次低成本收入或成本验证；` +
    `记录客户反馈、转化、毛收入、直接成本与投入时间。\n` +
    `当前目标联动：${day60GoalLink}\n` +
    `检查指标：${day60Metric}`;

  if (canonical !== expectedCanonical) return canonical;

  const mode =
    localizeWealthRecommendedMode(
      "en",
      recommendedMode,
    );

  const goal =
    day60GoalLink === "—"
      ? "—"
      : translatedNarrative(
          "sheet14",
          day60GoalLink,
        );

  const metric =
    day60Metric === "—"
      ? "—"
      : translatedNarrative(
          "sheet14",
          day60Metric,
        );

  if (
    mode === recommendedMode ||
    goal === undefined ||
    metric === undefined
  ) {
    return canonical;
  }

  return (
    `Complete one low-cost income or cost validation around “${mode}”; ` +
    `record customer feedback, conversion, gross income, direct costs, and time invested.\n` +
    `Current-goal linkage: ${goal}\n` +
    `Check metric: ${metric}`
  );
}

export function presentSheet25Day90Plan(
  locale,
  canonical,
  {
    day90GoalLink,
    day90Metric,
  } = {},
) {
  if (typeof canonical !== "string") return canonical;
  if (locale !== "en") return canonical;

  if (
    typeof day90GoalLink !== "string" ||
    typeof day90Metric !== "string"
  ) {
    return canonical;
  }

  const expectedCanonical =
    `保留有效动作，建立月度现金流、风险上限与目标复盘流程；` +
    `停止无法验证或超出承受能力的投入。\n` +
    `当前目标联动：${day90GoalLink}\n` +
    `检查指标：${day90Metric}`;

  if (canonical !== expectedCanonical) return canonical;

  const goal =
    day90GoalLink === "—"
      ? "—"
      : translatedNarrative(
          "sheet14",
          day90GoalLink,
        );

  const metric =
    day90Metric === "—"
      ? "—"
      : translatedNarrative(
          "sheet14",
          day90Metric,
        );

  if (
    goal === undefined ||
    metric === undefined
  ) {
    return canonical;
  }

  return (
    `Retain effective actions and establish a monthly process for cash flow, risk limits, and goal review; ` +
    `stop commitments that cannot be validated or exceed your capacity.\n` +
    `Current-goal linkage: ${goal}\n` +
    `Check metric: ${metric}`
  );
}

export function presentSheet25NumberCulture(
  locale,
  canonical,
  {
    r5CoreNumbers,
    r7AuxNumbers,
    r3LifeNumber,
    r9ReduceNumbers,
    r10RelationClass,
  } = {},
) {
  if (typeof canonical !== "string") return canonical;
  if (locale !== "en") return canonical;

  if (
    typeof r5CoreNumbers !== "string" ||
    typeof r7AuxNumbers !== "string" ||
    !validLifeNumber(r3LifeNumber) ||
    typeof r9ReduceNumbers !== "string" ||
    typeof r10RelationClass !== "string"
  ) {
    return canonical;
  }

  const expectedCanonical =
    `核心多用：${r5CoreNumbers}｜辅助：${r7AuxNumbers}｜` +
    `生命数字锚点：${r3LifeNumber}｜少用：${r9ReduceNumbers}\n` +
    `五行关系：${r10RelationClass}。` +
    `仅作传统文化与个人识别参考；` +
    `不代表彩票号码、不保证中奖或财富，` +
    `也不得凌驾于合法性、价格、隐私、安全、易记与实际需求。`;

  if (canonical !== expectedCanonical) return canonical;

  const relation =
    localizeNumberRelationClass(
      "en",
      r10RelationClass,
    );

  const relationKnown =
    r10RelationClass === "—" ||
    relation !== r10RelationClass;

  if (!relationKnown) return canonical;

  return (
    `Core preference: ${r5CoreNumbers} | ` +
    `Supporting: ${r7AuxNumbers} | ` +
    `Life Number anchor: ${r3LifeNumber} | ` +
    `Reduced-use: ${r9ReduceNumbers}\n` +
    `Five-Element relationship: ${relation}. ` +
    `For traditional-cultural and personal-identification reference only; ` +
    `this does not represent lottery numbers and does not guarantee winnings or wealth, ` +
    `and it must not override legality, price, privacy, security, memorability, or practical requirements.`
  );
}
